import type { google } from '@alugha/ima';
import { isNil } from 'ramda';
import videojs, { ContribAds, VideoJsPlayer } from 'video.js';

import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'contextPlugin';

export interface Options {
  initialBlockAd?: boolean;
}

export type State = {
  clickToPlayCount: number;
  clickToPauseCount: number;
  userVolumeChangeCount: number;
  adVolumeChangeCount: number;
  initialStartTime: number;
  isControllerTriggeredPause: boolean;
  isControllerTriggeredPlay: boolean;
  blockAd: boolean;
  resetImaCount: number;
  videoContentResumeCount: number;
  isImaRequestedAd: boolean;
  currentAdTagUrl: null | string;
  isGlobalGoogleImaReady: boolean;
  adTypesCompleted: ContribAds['adType'][];
  isCompleteAllAds: boolean;
  adErrorEvents: (google.ima.AdErrorEvent | videojs.EventTarget.Event)[];
};

interface ContextPlugin {
  setState: (state: Partial<State>) => void;
  state: State;
}

class ContextPlugin extends Plugin {
  static defaultState: State;
  constructor(player: VideoJsPlayer, options: Options) {
    super(player, options);

    attachPluginToPlayer(this, player);

    const { initialBlockAd = false } = options ?? {};

    this.setState({ blockAd: initialBlockAd });
  }

  public addClickToPlayCount() {
    this.setState({ clickToPlayCount: this.state.clickToPlayCount + 1 });
  }

  public addClickToPauseCount() {
    this.setState({ clickToPauseCount: this.state.clickToPauseCount + 1 });
  }

  public addUserVolumeChangeCount() {
    this.setState({
      userVolumeChangeCount: this.state.userVolumeChangeCount + 1,
    });
  }

  public addAdVolumeChangeCount() {
    this.setState({
      adVolumeChangeCount: this.state.adVolumeChangeCount + 1,
    });
  }

  public addResetImaCount() {
    this.setState({
      resetImaCount: this.state.resetImaCount + 1,
    });
  }

  public setIsImaRequestedAd(requested: boolean) {
    if (!isNil(requested)) {
      this.setState({
        isImaRequestedAd: requested,
      });
    }
  }

  public addAdTypeCompleted(adType: ContribAds['adType']) {
    this.setState({
      adTypesCompleted: [...(this.state.adTypesCompleted || []), adType],
    });
  }

  public addVideoContentResumeCount() {
    this.setState({
      videoContentResumeCount: (this.state.videoContentResumeCount || 0) + 1,
    });
  }

  public setCompleteAllAds(completed: boolean) {
    if (!isNil(completed)) {
      this.setState({
        isCompleteAllAds: completed,
      });
    }
  }

  public setCurrentAdTagUrl(adTagUrl: string | null) {
    this.setState({
      currentAdTagUrl: adTagUrl,
    });
  }

  public addAdErrorEvent(
    adErrorEvent: google.ima.AdErrorEvent | videojs.EventTarget.Event
  ) {
    this.setState({
      adErrorEvents: [...(this.state.adErrorEvents || []), adErrorEvent],
    });
  }

  public resetAdErrorEvents() {
    this.setState({
      adErrorEvents: ContextPlugin.defaultState.adErrorEvents,
    });
  }

  public resetInteractionStat() {
    this.setState({
      clickToPlayCount: ContextPlugin.defaultState.clickToPlayCount,
      clickToPauseCount: ContextPlugin.defaultState.clickToPauseCount,
      userVolumeChangeCount: ContextPlugin.defaultState.userVolumeChangeCount,
      adVolumeChangeCount: ContextPlugin.defaultState.adVolumeChangeCount,
    });
  }

  public resetAdsStat() {
    this.setState({
      videoContentResumeCount:
        ContextPlugin.defaultState.videoContentResumeCount,
      isImaRequestedAd: ContextPlugin.defaultState.isImaRequestedAd,
      currentAdTagUrl: ContextPlugin.defaultState.currentAdTagUrl,
      adTypesCompleted: ContextPlugin.defaultState.adTypesCompleted,
      isCompleteAllAds: ContextPlugin.defaultState.isCompleteAllAds,
      adErrorEvents: ContextPlugin.defaultState.adErrorEvents,
    });
  }

  public resetState() {
    this.resetInteractionStat();
    this.resetAdsStat();
  }
}

videojs.registerPlugin(PLUGIN_NAME, ContextPlugin);

ContextPlugin.defaultState = {
  clickToPlayCount: 0,
  clickToPauseCount: 0,
  userVolumeChangeCount: 0,
  adVolumeChangeCount: 0,
  initialStartTime: 0,
  isControllerTriggeredPause: false,
  isControllerTriggeredPlay: false,
  // ad related; no need to reset on resetting IMA
  blockAd: false,
  resetImaCount: 0,
  isGlobalGoogleImaReady: false,
  // ad related
  videoContentResumeCount: 0,
  isImaRequestedAd: true,
  currentAdTagUrl: null,
  adTypesCompleted: [],
  isCompleteAllAds: false,
  adErrorEvents: [],
};

export type ContextPluginFactoryFn = (options: Options) => ContextPlugin;

export default ContextPlugin;
